import React, { useState, useEffect } from 'react';
import { liveAPI, API_BASE } from '../api/client';
import { Video, Loader2, PlayCircle, Plus, X, Radio, Users } from 'lucide-react';
import { getTranslation as t } from '../utils/translations';
import { useAuth } from '../context/AuthContext';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { getUserRegionalPreferences } from '../utils/countryLanguageEngine';

export default function LiveRoomList({ onJoinRoom }) {
    const { user } = useAuth();
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [creating, setCreating] = useState(false);

    const canHost = (user?.roles || []).includes('ROOM_HOST') || (user?.roles || []).includes('SUPPORT_AGENT') || (user?.roles || []).includes('ADMIN') || (user?.roles || []).includes('SUPER_ADMIN');

    useEffect(() => {
        let interval;
        const fetchRooms = async () => {
            try {
                const userGeo = getUserRegionalPreferences();
                const clientCountry = (userGeo?.country || 'SA').toUpperCase();
                const data = await liveAPI.getActiveRooms(clientCountry);
                const filtered = (data || []).filter(r => {
                    if (!r.availableCountries || r.availableCountries.length === 0) return true;
                    const countriesList = r.availableCountries.map(c => String(c).toUpperCase());
                    return countriesList.includes('ALL') || countriesList.includes(clientCountry);
                });
                setRooms(filtered);
            } catch (e) {
                console.error('Failed fetching live rooms', e);
            } finally {
                setLoading(false);
            }
        };

        fetchRooms();
        interval = setInterval(fetchRooms, 30000); // Fallback poll every 30s

        const handleGeoChange = () => fetchRooms();
        window.addEventListener('geoContextChange', handleGeoChange);

        const stompClient = new Client({
            webSocketFactory: () => new SockJS(`${API_BASE}/ws/chat`),
            onConnect: () => {
                stompClient.subscribe('/topic/live-rooms', () => {
                    fetchRooms();
                });
            }
        });
        stompClient.activate();

        return () => {
            clearInterval(interval);
            stompClient.deactivate();
            window.removeEventListener('geoContextChange', handleGeoChange);
        };
    }, []);

    const handleCreateRoom = async (e) => {
        e.preventDefault();
        if (!title.trim()) return;

        try {
            setCreating(true);
            const newRoom = await liveAPI.createRoom({ title, description });
            setShowCreateForm(false);
            setTitle('');
            setDescription('');
            // Ensure they immediately enter as the Host locally
            onJoinRoom(newRoom.id, true);
        } catch (error) {
            console.error('Failed to create room:', error);
            alert('Failed to create room. Please ensure your account has the correct permissions.');
        } finally {
            setCreating(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center p-12">
                <Loader2 className="animate-spin text-lux-gold" size={32} />
            </div>
        );
    }


    return (
        <section className="section container">
            <div className="section-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Radio className="trust-pill" style={{ color: '#ef4444', animation: 'pulse 2s infinite' }} size={24} />
                    <h2 className="section-title">{t('SUPPORT_ROOMS_TITLE')}</h2>
                </div>
                {canHost && (
                    <button
                        onClick={() => setShowCreateForm(true)}
                        className="btn-primary"
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px' }}
                    >
                        <Plus size={18} /> {t('CREATE_SUPPORT_ROOM')}
                    </button>
                )}
            </div>

            {showCreateForm && (
                <div className="glass-panel" style={{ padding: '24px', marginBottom: '32px', position: 'relative' }}>
                    <button
                        onClick={() => setShowCreateForm(false)}
                        style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', color: '#888', cursor: 'pointer' }}
                    >
                        <X size={20} />
                    </button>
                    <h3 style={{ color: '#c8a96a', marginBottom: '16px', fontSize: '1.2rem' }}>{t('START_SUPPORT_SESSION')}</h3>
                    <form onSubmit={handleCreateRoom} style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '500px' }}>
                        <div>
                            <label style={{ display: 'block', color: '#888', fontSize: '0.9rem', marginBottom: '8px' }}>{t('ROOM_TITLE')}</label>
                            <input
                                type="text"
                                required
                                style={{ width: '100%', background: '#000', border: '1px solid #333', borderRadius: '8px', padding: '10px 16px', color: '#fff', outline: 'none' }}
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder={t('ROOM_TITLE_PLACEHOLDER')}
                                disabled={creating}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', color: '#888', fontSize: '0.9rem', marginBottom: '8px' }}>{t('DESCRIPTION_OPTIONAL')}</label>
                            <textarea
                                style={{ width: '100%', background: '#000', border: '1px solid #333', borderRadius: '8px', padding: '10px 16px', color: '#fff', outline: 'none', minHeight: '80px' }}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder={t('ROOM_DESC_PLACEHOLDER')}
                                disabled={creating}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={creating || !title.trim()}
                            className="btn-primary"
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: (creating || !title.trim()) ? 0.5 : 1 }}
                        >
                            {creating ? <Loader2 className="spinner" size={18} /> : <Radio size={18} />}
                            {t('START_NOW')}
                        </button>
                    </form>
                </div>
            )}

            {rooms.length === 0 && !showCreateForm && (
                <div className="glass-panel" style={{ textAlign: 'center', padding: '48px 24px', borderStyle: 'dashed', borderColor: '#333' }}>
                    <Radio style={{ margin: '0 auto 16px', color: '#555' }} size={48} />
                    <h3 style={{ color: '#fff', fontSize: '1.2rem', marginBottom: '8px' }}>{t('NO_ACTIVE_SUPPORT_ROOMS')}</h3>
                    <p style={{ color: '#888' }}>{t('WAIT_FOR_SUPPORT_HOST')}</p>
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
                {rooms.map(room => (
                    <div key={room.id} className="product-card glass-panel" style={{ padding: '24px', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', fontSize: '0.75rem', fontWeight: 'bold', padding: '6px 12px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444', animation: 'pulse 2s infinite' }} /> LIVE NOW
                            </div>
                            <div style={{ color: '#888', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px', background: '#111', padding: '6px 12px', borderRadius: '20px' }}>
                                <Users size={12} /> {room.participants?.length || 0}/11
                            </div>
                        </div>

                        <h3 className="product-title" style={{ fontSize: '1.25rem', marginBottom: '8px' }}>{room.title}</h3>
                        <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '24px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{room.description}</p>

                        <button
                            onClick={() => onJoinRoom(room.id)}
                            style={{
                                width: '100%',
                                background: 'rgba(200, 169, 106, 0.1)',
                                color: '#c8a96a',
                                border: '1px solid rgba(200, 169, 106, 0.3)',
                                padding: '12px',
                                borderRadius: '8px',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.3s'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.background = '#c8a96a'; e.currentTarget.style.color = '#000'; }}
                            onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(200, 169, 106, 0.1)'; e.currentTarget.style.color = '#c8a96a'; }}
                        >
                            <PlayCircle size={20} /> {t('ENTER_ROOM')}
                        </button>
                    </div>
                ))}
            </div>
        </section>
    );
}
